import React, {
  CSSProperties,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Checkbox, Input, SemanticICONS } from "semantic-ui-react";

import moment from "moment";

// CSS
import "react-lib/css/DropdownOptions.scss";

// Types
type ExtendedDropdownItem = {
  key?: number | string;
  child?: boolean;
  children?: (number | string)[];
  content?: React.ReactNode;
  groupId?: number;
  parent?: boolean;
  text?: string;
  value: number | string;
  subOptions?: ExtendedDropdownItem[];
};

export type DropdownOptionsProps = {
  id?: string;
  style?: CSSProperties;
  disabled?: boolean;
  fluid?: boolean;
  icon?: SemanticICONS;
  iconStyle?: CSSProperties;
  inputStyle?: CSSProperties;
  menuStyle?: CSSProperties;
  multiple?: boolean;
  name?: number | string;
  placeholder?: string;
  search?: boolean;
  value?: (number | string)[] | number[] | string[] | number | string;
  options?: Record<string, any>[];
  showSubOptions?: boolean;
  // config
  checked?: boolean;
  clearable?: boolean;
  filter?: boolean;
  formatTimeOnBlur?: boolean;
  inline?: boolean;
  noMinWidth?: boolean;
  open?: boolean;
  searchBox?: boolean;
  searchChildren?: boolean;
  text?: string;
  visible?: boolean;
  // SearchBox mode
  startSearchLength?: number;
  type?: string;
  // callback
  onEvent?: (e: any) => void;
  onAdditionalUrlParams?: (searchText: string) => Record<string, any>;
  onChange?: (event: SyntheticEvent<HTMLElement>, data: any) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onFormatBeforeChange?: (value: string) => string;
};

const IconStyle: CSSProperties = {
  cursor: "pointer",
  height: "auto",
  margin: "-0.78571429em",
  opacity: 0.8,
  padding: "0.91666667em",
  position: "absolute",
  right: "0.75em",
  top: "calc(50% - 25%)",
  transition: "opacity .1s ease",
  width: "auto",
  zIndex: 3,
};

const DropdownOptions = React.forwardRef<any, DropdownOptionsProps>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<any>();
  const [inputValue, setInputValue] = useState<any>("");
  const [options, setOptions] = useState<ExtendedDropdownItem[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputSearchRef = useRef<any>(null);
  const isFocus = useRef<boolean>(false);
  const searchTimeoutRef = useRef<any>(null);

  useImperativeHandle<any, any>(ref, () => inputRef.current);

  const isSearchBoxMode = Boolean(props.type && props.id && props.onEvent);

  const allOptions = useMemo(() => {
    const optionItems = props.options as ExtendedDropdownItem[] | undefined;

    if (!props.showSubOptions) {
      return optionItems ?? [];
    }

    if (!optionItems) {
      return [];
    }

    const result = [];

    for (const [index, option] of optionItems.entries()) {
      const groupId = index + 1;

      // Add parent option
      result.push({
        ...option,
        children: (option.subOptions ?? []).map((item) => item.value),
        groupId,
        parent: Boolean(option.subOptions?.length),
      });

      // Add child options
      if (option.subOptions) {
        for (const subOption of option.subOptions) {
          result.push({
            ...subOption,
            child: true,
            groupId,
          });
        }
      }
    }

    return result;
  }, [props.options, props.showSubOptions]);

  const optionsWithoutParent = useMemo(
    (): ExtendedDropdownItem[] => allOptions.filter((option) => !option.parent),
    [allOptions]
  );

  const filterOptions = useMemo((): ExtendedDropdownItem[] => {
    if (props.filter && Array.isArray(currentValue)) {
      return options.filter((option) => !currentValue.includes(option.value));
    }

    return options;
  }, [currentValue, options, props.filter]);

  const valueText = useMemo(
    () => allOptions.find((item) => item.value === currentValue)?.text || "",
    [allOptions, currentValue]
  );

  const isSearch = useMemo(
    () => Boolean(props.search && !props.multiple && !props.formatTimeOnBlur),
    [props.formatTimeOnBlur, props.multiple, props.search]
  );

  const handleSearchBoxSearch = useCallback(
    (searchText: string) => {
      if (!isSearchBoxMode || !props.onEvent) {
        return;
      }

      const minLength = props.startSearchLength ?? 3;

      if (searchText.length < minLength) {
        return;
      }

      const params: Record<string, any> = {
        id: props.id,
        searchText,
        type: props.type,
      };

      if (props.onAdditionalUrlParams) {
        const urlParams = props.onAdditionalUrlParams(searchText);

        params.urlParams = urlParams;
      }

      props.onEvent({ message: "ItemSearch", params });
    },
    [isSearchBoxMode]
  );

  const handleBlur = () => {
    setOpen(false);
    inputRef.current?.blur();

    if (!props.formatTimeOnBlur) {
      setOptions(allOptions);
    }
  };

  const handleMouseClick = () => {
    if (isFocus.current || !open) {
      return;
    }

    if (isSearch) {
      setInputValue(valueText);
    }

    if (props.formatTimeOnBlur) {
      formatTime({ target: { value: inputRef.current?.value } });
    }

    handleBlur();
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("click", handleMouseClick);
    } else {
      document.removeEventListener("click", handleMouseClick);
    }

    return () => {
      document.removeEventListener("click", handleMouseClick);
    };
  }, [handleMouseClick, open]);

  useEffect(() => {
    if (props.value !== undefined) {
      setCurrentValue(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (props.multiple) {
      if (!Array.isArray(currentValue)) {
        setCurrentValue((props.value ? [props.value] : []).flat());
      }
    } else {
      setInputValue(valueText);
    }
  }, [currentValue, props.multiple, valueText]);

  useEffect(() => {
    setOptions(allOptions);
  }, [allOptions]);

  // Callback
  const getChecked = useCallback(
    (data: ExtendedDropdownItem): boolean => {
      if (data.parent) {
        return Array.isArray(currentValue)
          ? data.children?.every((id: number | string) => currentValue.includes(id)) ?? false
          : false;
      }

      return Array.isArray(currentValue)
        ? currentValue.includes(data.value)
        : data.value === currentValue;
    },
    [currentValue]
  );

  // Use Memo
  // Check ว่ามีการถูกเลือกหรือไม่
  const isSelected = useMemo(
    () =>
      Boolean(
        (props.multiple && Array.isArray(currentValue) ? currentValue.length : currentValue) &&
          !props.formatTimeOnBlur
      ),
    [currentValue, props.formatTimeOnBlur, props.multiple]
  );

  const isPlaceholderValue = useMemo(
    () => Boolean(open && props.search && !props.multiple),
    [open, props.multiple, props.search]
  );

  const placeholderValue = useMemo(() => {
    if (!isSelected) {
      return props.placeholder;
    }

    if (isPlaceholderValue) {
      return valueText;
    }

    return "";
  }, [isPlaceholderValue, isSelected, props.placeholder, valueText]);

  // Handler
  // Format time, ใช้สำหรับ TimeComboBox
  const formatTime = (e: any) => {
    const inputCurrentValue = inputRef.current?.value;

    if (!inputCurrentValue) {
      handleChangeValue(inputCurrentValue)(e);

      return;
    }

    const shiftZero =
      inputCurrentValue.length === 1 || /^\d\D/.test(inputCurrentValue)
        ? `0${inputCurrentValue}`
        : inputCurrentValue;

    const cleanText = shiftZero.replaceAll(/\D+/g, "").padEnd(4, "0");
    const overTime = Number(cleanText[0] + cleanText[1]) > 24 ? `0${cleanText}` : cleanText;
    const overMinute =
      Number(overTime[2] + overTime[3]) > 59
        ? `${Number(overTime[0] + overTime[1]) + 1}`.padStart(2, "0") +
          `${Number(overTime[2] + overTime[3]) - 60}`.padStart(2, "0")
        : overTime;
    const overHour = (Number(overMinute[0] + overMinute[1]) % 24).toString().padStart(2, "0");
    const time = `${overHour[0]}${overHour[1]}${overMinute[2]}${overMinute[3] || 0}`;
    const timeFormat = `${time[0]}${time[1]}:${time[2]}${time[3]}`;

    // บังคับ format inputValue ทุกครั้งที่ blur
    setInputValue(timeFormat);
    handleChangeValue(timeFormat)(e);
  };

  const handleFocus = (e?: FocusEvent<HTMLInputElement>) => {
    if (props.disabled || props.visible === false) {
      return;
    }

    isFocus.current = true;

    if (props.open !== false) {
      setOpen(true);
    }

    if (isSearch) {
      setInputValue("");
    }

    if (e) {
      props.onFocus?.(e);
    }

    setTimeout(() => {
      isFocus.current = false;
    }, 200);
  };

  const handleChangeInput = (e: any) => {
    if (props.disabled || props.visible === false) {
      return;
    }

    const inputCurrentValue = e.target.value as string;

    setInputValue(inputCurrentValue);

    // SearchBox mode
    if (isSearchBoxMode) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        handleSearchBoxSearch(inputCurrentValue);
      }, 250);

      return;
    }

    // Normal search mode
    if (!props.search || props.formatTimeOnBlur) {
      return;
    }

    let search = allOptions.filter((item) =>
      item.text?.toLowerCase().includes(inputCurrentValue.toLowerCase())
    );

    if (props.showSubOptions) {
      const groupIds = new Set(search.map((item) => item.groupId));

      if (props.searchChildren) {
        const parentValues = new Set(
          allOptions
            .filter((option) => groupIds.has(option.groupId) && option.parent)
            .map((option) => option.value)
        );

        search = allOptions.filter(
          (option) =>
            parentValues.has(option.value) ||
            option.text?.toLowerCase().includes(inputCurrentValue.toLowerCase())
        );
      } else {
        search = allOptions.filter((option) => groupIds.has(option.groupId));
      }
    }

    setOptions(search);
  };

  const handleChangeValue = (optionValue: any, data?: any) => (e: SyntheticEvent<HTMLElement>) => {
    const itemData = data?.item as ExtendedDropdownItem | undefined;
    const children = itemData?.children ?? [];
    const isParent = itemData?.parent ?? false;
    const id = itemData?.value ?? optionValue;

    let updatedValue: string[] | string = id;

    if (props.multiple && Array.isArray(currentValue)) {
      if (isParent && data?.checked) {
        updatedValue = [...new Set([...currentValue, ...children])];
      } else if (isParent && !data?.checked) {
        updatedValue = currentValue.filter((acc) => !children.includes(acc));
      } else if (currentValue.includes(id)) {
        updatedValue = currentValue.filter((acc) => acc !== id);
      } else if (!data.clearable) {
        updatedValue = [...currentValue, id];
      }
    }

    const formatValue = updatedValue;

    if (props.value === undefined) {
      setCurrentValue(formatValue);

      return;
    }

    const formattedValue = props.onFormatBeforeChange?.(String(formatValue)) ?? formatValue;

    if (!formattedValue && props.onFormatBeforeChange) {
      setInputValue("");
    }

    props.onChange?.(e, { name: props.name, value: formattedValue });
  };

  const handlePrevent = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClickCombobox = () => {
    // เพื่อ ให้ focus input และ open dropdown
    if (props.multiple && props.search) {
      inputRef.current?.focus();

      if (props.searchBox) {
        const inputElement = inputSearchRef.current?.inputRef?.current as HTMLInputElement | null;

        setTimeout(() => {
          inputElement?.focus();
        });
      }
    } else if (!props.search) {
      handleFocus();
    }
  };

  const handleRemoveValue = (removeValue: any, data: any) => (e: any) => {
    handleChangeValue(removeValue, data)(e);
  };

  const handleSelected = (selectedValue: any, data: any) => (e: SyntheticEvent<HTMLElement>) => {
    if (data?.item?.parent && !props.multiple) {
      return;
    }

    if (props.multiple && props.search) {
      handleChangeInput({ target: { value: "" } });
    }

    handleChangeValue(selectedValue, data)(e);

    // close on selected
    if (!props.multiple) {
      if (isSearch) {
        setInputValue(valueText);
      }

      handleBlur();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const input = (e.target as HTMLInputElement).value;

    if (
      e.key === "Backspace" &&
      !input &&
      props.multiple &&
      props.search &&
      Array.isArray(currentValue) &&
      currentValue[0]
    ) {
      handleChangeValue(currentValue.slice(-1)[0])(e);
    }
  };

  const getDropdownClasses = () => {
    const classes = ["ui"];

    if (open) {
      classes.push("active", "visible");
    }

    if (props.multiple) {
      classes.push("multiple");
    }

    if (props.search) {
      classes.push("search");
    }

    if (props.fluid) {
      classes.push("fluid");
    }

    if (props.disabled) {
      classes.push("disabled");
    }

    classes.push("selection", "dropdown");

    return classes.join(" ");
  };

  const getInputStyle = () => {
    const baseStyle = {
      width: isSelected ? "" : "100%",
    };

    if (isSelected && (props.searchBox || (props.inline && props.multiple))) {
      return {
        ...baseStyle,
        opacity: 0,
        pointerEvents: "none" as const,
        position: "absolute" as const,
      };
    }

    return baseStyle;
  };

  const getContainerStyle = () => {
    const baseStyle = {
      minWidth: props.noMinWidth || props.fluid ? "" : "auto",
      width: "100%",
      ...props.inputStyle,
    };

    if (props.disabled) {
      return {
        ...baseStyle,
        borderColor: "rgba(34,36,38,.15)",
        opacity: 0.5,
      };
    }

    return baseStyle;
  };

  const getIconClass = () => {
    if (isSelected && props.clearable && !props.inline) {
      return "clear icon";
    }

    return `${props.icon || "dropdown"} icon`;
  };

  const getIconStyle = () => ({
    ...IconStyle,
    cursor: props.disabled ? "auto" : "pointer",
    opacity: props.disabled ? 0.65 : 0.45,
    ...props.iconStyle,
  });

  return (
    <div
      className={`DropdownOptions ${props.inline ? "--inline-label" : ""}`}
      style={{ ...props.style }}
    >
      <div
        id={props.id || `DropdownOptions-Div`}
        className={getDropdownClasses()}
        style={getContainerStyle()}
        aria-controls="listbox1"
        aria-expanded={open}
        aria-label="options custom"
        onClick={handleClickCombobox}
      >
        {props.text ? (
          !inputValue && (
            <div className="divider text" role="alert">
              {props.text}
            </div>
          )
        ) : (
          <LabelList
            multiple={props.multiple}
            optionsWithoutParent={optionsWithoutParent}
            value={currentValue}
            onClick={(e: any, data: any) => {
              isFocus.current = true;
              handleRemoveValue(data.value, data)(e);
            }}
            onFocus={() => (isFocus.current = true)}
            onMouseLeave={() => (isFocus.current = false)}
            onMouseOver={() => (isFocus.current = true)}
            onPrevent={handlePrevent}
          />
        )}

        {(props.visible !== false || (props.placeholder && !currentValue?.length)) && (
          <input
            ref={inputRef}
            id={props.id ? `${props.id}-Input` : `DropdownOptions-Input`}
            className="search"
            style={getInputStyle()}
            autoComplete="off"
            disabled={props.disabled}
            placeholder={placeholderValue}
            readOnly={!props.search}
            type="text"
            value={props.searchBox ? "" : inputValue}
            onChange={handleChangeInput}
            onClick={() => (isFocus.current = Boolean(props.search))}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onMouseLeave={() => (isFocus.current = false)}
            onMouseOver={() => (isFocus.current = Boolean(props.search))}
          />
        )}
        <i
          id={props.id ? `${props.id}-i` : "DropdownOptions-i"}
          className={getIconClass()}
          style={getIconStyle()}
          aria-hidden="true"
          onClick={(e) => {
            if (!props.disabled) {
              if (props.formatTimeOnBlur) {
                handlePrevent(e as any);
                handleChangeValue(moment().format("HH:mm"))(e);
              } else if (isSelected && !props.inline && props.clearable) {
                handleRemoveValue(props.multiple ? [] : "", { clearable: true })(e);
              } else if (!open && !props.clearable) {
                inputRef.current?.focus();
              }
            }
          }}
        />
        <div
          id="listbox1"
          className={`menu transition ${open ? "visible" : ""}`}
          style={props.menuStyle}
          aria-hidden="true"
          aria-multiselectable={props.multiple ? "true" : "false"}
          onClick={() => (isFocus.current = true)}
          onFocus={() => (isFocus.current = true)}
          onMouseLeave={() => (isFocus.current = false)}
          onMouseOver={() => (isFocus.current = true)}
        >
          <Input
            ref={inputSearchRef}
            style={{
              display: props.searchBox ? "" : "none",
              margin: "0.35rem 1rem 0.25rem",
              width: "calc(100% - 2rem)",
            }}
            value={inputValue}
            fluid
            onChange={handleChangeInput}
          />
          <div
            style={
              props.showSubOptions && props.searchBox
                ? {
                    maxHeight: props.menuStyle?.maxHeight,
                    overflowX: "hidden",
                    paddingBottom: props.menuStyle?.maxHeight
                      ? props.menuStyle.paddingBottom || "2.75rem"
                      : "",
                  }
                : {}
            }
          >
            {options.length === 0 && props.search ? (
              <div className="message" style={{ padding: ".78571429rem 1.14285714rem" }}>
                No results found.
              </div>
            ) : (
              filterOptions.map((item) => {
                const checked = getChecked(item);

                return (
                  <div
                    key={`time-${item.key}`}
                    className={`${checked ? "active" : ""}${
                      checked && !props.checked ? ` selected` : ""
                    } item`}
                    style={{ pointerEvents: "all" }}
                    aria-hidden="true"
                    aria-selected={checked}
                    onClick={handleSelected(item.value, {
                      checked: !checked,
                      item,
                    })}
                  >
                    <span
                      className="text"
                      style={{
                        paddingLeft: item.child ? "17.5px" : "",
                      }}
                    >
                      {!props.checked || (item.parent && !props.multiple) ? (
                        item.content ?? item.text
                      ) : (
                        <Checkbox
                          style={{ marginRight: "0.5rem" }}
                          checked={checked}
                          label={item.content ?? item.text}
                          onChange={handleSelected(item.value, {
                            checked: !checked,
                            item,
                          })}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

/* ------------------------------------------------------ */
/*                       LabelList                        */
/* ------------------------------------------------------ */
type LabelListProps = {
  multiple?: boolean;
  optionsWithoutParent?: ExtendedDropdownItem[];
  value?: (number | string)[];
  onClick?: (e: React.MouseEvent, data: ExtendedDropdownItem) => void;
  onFocus?: () => void;
  onMouseLeave?: () => void;
  onMouseOver?: () => void;
  onPrevent?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

const LabelList: React.FC<LabelListProps> = (props) => {
  const handleClick = (data: ExtendedDropdownItem) => (e: React.MouseEvent) => {
    props.onClick?.(e, data);
  };

  if (!props.multiple || !Array.isArray(props.value)) {
    return null;
  }

  return (
    <span>
      {props.value.map((v: number | string) => {
        const data = props.optionsWithoutParent?.find(
          (item: ExtendedDropdownItem) => item.value === v
        );

        if (!data) {
          return null;
        }

        return (
          <a
            key={data.key || data.value}
            className="ui label"
            aria-hidden="true"
            onClick={props.onPrevent}
          >
            {data.text}
            <i
              className="delete icon"
              aria-hidden="true"
              onClick={handleClick(data)}
              onFocus={props.onFocus}
              onMouseLeave={props.onMouseLeave}
              onMouseOver={props.onMouseOver}
            />
          </a>
        );
      })}
    </span>
  );
};

DropdownOptions.displayName = "DropdownOptions";
export default React.memo(DropdownOptions);
